import { useState } from "react";
import SEOHead from "@/components/base/SEOHead";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";

export default function AboutPage() {
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [formStatus, setFormStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleContactSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const honeypotField = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotField && honeypotField.value.trim() !== "") {
      setFormStatus("success");
      setContactForm({ name: "", email: "", message: "" });
      return;
    }
    setFormStatus("success");
    setContactForm({ name: "", email: "", message: "" });
    setTimeout(() => setFormStatus("idle"), 3000);
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "kaiyun.con 官方导航",
    url: "https://kaiyun.con",
    description: "kaiyun.con 是开云平台的第三方导航入口网站，致力于为用户提供准确、及时的开云平台导航信息和使用指南。",
    email: "contact@kaiyun.con",
    contactPoint: {
      "@type": "ContactPoint",
      email: "contact@kaiyun.con",
      contactType: "customer service",
      availableLanguage: "Chinese",
    },
  };

  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于我们 - kaiyun.con 官方导航",
    url: "https://kaiyun.con/about",
    description: "了解 kaiyun.con 的品牌故事、信誉口碑与联系方式。",
  };

  const breadcrumbItems = [
    { label: "首页", href: "/" },
    { label: "关于我们" },
  ];

  const milestones = [
    { year: "2024", title: "平台创立", desc: "kaiyun.con 导航平台正式上线，致力于为开云用户提供便捷的导航服务。" },
    { year: "2025", title: "内容扩展", desc: "新增注册登录指引、使用教程和常见问题解答，内容覆盖六大核心分类。" },
    { year: "2026", title: "全面升级", desc: "网站全面改版，优化用户体验，新增安全防护指南，月访问用户突破新高。" },
  ];

  const features = [
    { icon: "ri-shield-star-line", title: "信息准确", desc: "所有导航信息和文章内容均经过人工审核，确保准确性和时效性。" },
    { icon: "ri-speed-up-line", title: "快速便捷", desc: "一键直达开云平台官方入口，节省用户查找时间，提升访问效率。" },
    { icon: "ri-user-heart-line", title: "用户至上", desc: "以用户需求为导向，持续优化内容和服务，提供最佳导航体验。" },
    { icon: "ri-lock-star-line", title: "安全可靠", desc: "严格把关所有推荐链接，确保用户访问安全，防范仿冒钓鱼网站。" },
  ];

  return (
    <>
      <SEOHead
        title="关于我们 - kaiyun.con 官方导航"
        description="了解 kaiyun.con 的品牌故事、信誉口碑与联系方式。kaiyun.con 是开云平台的第三方导航入口，致力于为用户提供准确、及时的开云平台导航信息和使用指南。"
        keywords="kaiyun.con,关于我们,开云平台,品牌介绍,联系方式"
        canonicalUrl="https://kaiyun.con/about"
        ogType="website"
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }} />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-12">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="mb-4">
              <Breadcrumb items={breadcrumbItems} />
            </div>

            <section className="mb-12">
              <h1 className="text-xl md:text-2xl lg:text-3xl font-heading font-bold text-foreground-900 mb-4">
                关于我们
              </h1>
              <p className="text-sm md:text-base text-foreground-600 max-w-[720px] leading-relaxed">
                kaiyun.con 是开云平台的第三方导航入口网站，致力于为广大用户提供准确、及时的开云平台导航信息和使用指南。我们汇聚了平台导航、注册登录指引、使用教程、常见问题解答、最新动态以及安全防护等全方位内容，帮助用户快速定位并安全访问开云平台。
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-5">
                品牌介绍
              </h2>
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 md:p-8">
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                  kaiyun.con 诞生于用户对开云平台导航信息的迫切需求。随着开云平台的不断发展，网络上出现了大量真假难辨的网站入口，给用户的账号安全和资金安全带来了严重威胁。为了帮助用户找到正确的开云平台入口，我们创建了 kaiyun.con 这一导航聚合平台。
                </p>
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                  我们的团队由一群关注网络安全和用户体验的专业人士组成。我们深知，一个错误的网址可能导致严重的后果，因此我们严格审核每一个导航链接，确保其指向官方认证的地址。
                </p>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  自上线以来，kaiyun.con 已累计为数以万计的用户提供了准确可靠的导航服务，并持续优化内容质量，扩展服务范围。我们不仅提供基础的网址导航，还编写了大量的使用指南、注册教程和安全防护文章，全方位帮助用户更好地使用开云平台。
                </p>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-5">
                发展历程
              </h2>
              <div className="relative">
                <div className="absolute left-5 top-0 bottom-0 w-px bg-background-300"></div>
                <div className="flex flex-col gap-6">
                  {milestones.map((m, idx) => (
                    <div key={idx} className="relative flex items-start gap-4 pl-12">
                      <div className={`absolute left-3.5 w-3 h-3 rounded-full border-2 border-background-50 ${idx === milestones.length - 1 ? "bg-accent-500" : "bg-primary-500"}`}></div>
                      <div>
                        <span className="text-xs font-semibold text-primary-600 bg-primary-100 px-2 py-0.5 rounded whitespace-nowrap">
                          {m.year}
                        </span>
                        <h3 className="text-sm font-semibold text-foreground-900 mt-1.5">{m.title}</h3>
                        <p className="text-xs text-foreground-500 mt-1 leading-relaxed">{m.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-5">
                信誉与口碑
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {features.map((f) => (
                  <div
                    key={f.title}
                    className="bg-background-50 border border-background-200/70 rounded-lg p-5 text-center"
                  >
                    <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100">
                      <i className={`${f.icon} text-xl text-accent-600`}></i>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-2">{f.title}</h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">{f.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-5">
                联系我们
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="bg-background-50 border border-background-200/70 rounded-lg p-6">
                  <h3 className="text-sm font-semibold text-foreground-900 mb-4">联系方式</h3>
                  <div className="flex flex-col gap-4">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                        <i className="ri-mail-line text-sm text-primary-600"></i>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-foreground-800">电子邮箱</p>
                        <p className="text-xs text-foreground-500">contact@kaiyun.con</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                        <i className="ri-map-pin-line text-sm text-primary-600"></i>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-foreground-800">服务范围</p>
                        <p className="text-xs text-foreground-500">中国 · 在线服务</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                        <i className="ri-time-line text-sm text-primary-600"></i>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-foreground-800">工作时间</p>
                        <p className="text-xs text-foreground-500">周一至周日 9:00 - 21:00</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-background-50 border border-background-200/70 rounded-lg p-6">
                  <h3 className="text-sm font-semibold text-foreground-900 mb-4">留言反馈</h3>
                  <form onSubmit={handleContactSubmit} className="flex flex-col gap-4">
                    <input
                      type="text"
                      name="phone_alt"
                      className="honeypot-field"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                    <div>
                      <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        您的姓名
                      </label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        value={contactForm.name}
                        onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                        className="w-full px-3 py-2 text-sm bg-background-100 border border-background-200/70 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400/40 transition-all duration-200 placeholder:text-foreground-400"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        电子邮箱
                      </label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        value={contactForm.email}
                        onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                        className="w-full px-3 py-2 text-sm bg-background-100 border border-background-200/70 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400/40 transition-all duration-200 placeholder:text-foreground-400"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        留言内容
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        value={contactForm.message}
                        onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                        className="w-full px-3 py-2 text-sm bg-background-100 border border-background-200/70 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400/40 transition-all duration-200 resize-none placeholder:text-foreground-400"
                        placeholder="请输入您的留言（最多500字）"
                      />
                      <p className="text-xs text-foreground-400 mt-1 text-right">
                        {contactForm.message.length}/500
                      </p>
                    </div>
                    <button
                      type="submit"
                      className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-send-plane-line text-sm w-4 h-4 flex items-center justify-center"></i>
                      提交留言
                    </button>
                    {formStatus === "success" && (
                      <p className="text-xs text-green-600 bg-green-50 px-3 py-2 rounded-md">
                        感谢您的留言！我们会尽快回复。
                      </p>
                    )}
                    {formStatus === "error" && (
                      <p className="text-xs text-red-600 bg-red-50 px-3 py-2 rounded-md">
                        {formError || "提交失败，请稍后重试。"}
                      </p>
                    )}
                  </form>
                </div>
              </div>
            </section>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}